/**
 * Protected Layout
 * Main application layout for authenticated users
 * Includes AppBar, Drawer, and main content area with responsive behavior
 */

import { Box, useTheme, useMediaQuery } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { AppBar } from '@/components/layout/AppBar';
import { NavigationDrawer, DRAWER_WIDTH_COLLAPSED } from '@/components/layout/NavigationDrawer';
import { BottomNav, BOTTOM_NAV_HEIGHT } from '@/components/layout/BottomNav';
import { SkipLink } from '@/components/common/SkipLink';
import { SessionTimeoutWarning } from '@/features/auth/components';
import { useTokenRefresh } from '@/hooks/useTokenRefresh';

/**
 * Protected Layout (Main Layout)
 * Main application shell for authenticated pages
 *
 * Responsive behavior:
 * - xs/sm (< lg): Full-width content, no drawer
 * - lg+ (1200px+): Content with fixed 72px drawer margin
 */
export const ProtectedLayout: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  // Enable proactive token refresh for authenticated users
  useTokenRefresh();

  // Fixed margin: 72px on desktop, 0 on mobile
  const mainMarginLeft = isDesktop ? DRAWER_WIDTH_COLLAPSED : 0;

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      {/* Skip to main content link (accessibility) */}
      <SkipLink targetId="main-content" />

      {/* AppBar */}
      <AppBar />

      {/* Navigation Drawer */}
      <NavigationDrawer />

      {/* Main Content Area */}
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          mt: { xs: 7, md: 8 }, // Account for AppBar height
          ml: `${mainMarginLeft}px`,
          minHeight: 'calc(100vh - 64px)', // Full height minus AppBar
          pb: { xs: `${BOTTOM_NAV_HEIGHT + 8}px`, lg: 0 }, // Padding for bottom nav
          outline: 'none', // Remove focus outline (skip link handles focus)
        }}
      >
        <Outlet />
      </Box>

      {/* Bottom Navigation (mobile only) */}
      <BottomNav />

      {/* Session timeout warning modal */}
      <SessionTimeoutWarning />
    </Box>
  );
};

/**
 * Export as MainLayout for consistency
 */
export const MainLayout = ProtectedLayout;
